// src/services/rbacService.ts
// Tenant-facing dynamic RBAC (Model A): custom role builder + role assignment.
//
// These routes are served by the gateway at /api/v1/rbac/* and are tenant-scoped
// SERVER-SIDE from the session — we NEVER send a tenantId. Auth rides the httpOnly
// session cookie (withCredentials) + the session-bound CSRF token on writes, exactly
// like lib/aiConfigApi.ts and the /api/v1/users surface the UsersRolesPage consumes.
import axios from 'axios';
import { attachCookieAuth } from '@/lib/session';

const AUTH_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

function authed(baseURL: string) {
  const i = axios.create({ baseURL, timeout: 30000, withCredentials: true });
  i.interceptors.request.use(attachCookieAuth);
  return i;
}
const api = authed(AUTH_BASE);
const BASE = '/api/v1/rbac';

// ---- Types (mirror the platform catalog shape) ----------------------------
export type RoleScope = 'self' | 'direct_reports' | 'team' | 'tenant';

export interface CatalogPermission {
  key: string;
  description?: string;
  dataScope?: string;
}
export interface CatalogService {
  service: string;
  permissions: CatalogPermission[];
}
export interface RbacCatalog {
  services: CatalogService[];
}

export interface Role {
  id: string;
  key: string;
  label: string;
  description?: string;
  scope: RoleScope;
  permissions: string[];
  isSystem?: boolean;
}
export interface RolesResponse {
  systemRoles: Role[];
  customRoles: Role[];
}

export interface CreateRoleInput {
  key: string;
  label: string;
  description?: string;
  scope: RoleScope;
  permissions: string[];
}
export interface UpdateRoleInput {
  label?: string;
  description?: string;
  permissions?: string[];
}

// An assignment row as returned by GET /rbac/assignments?userId=…. The exact
// envelope is not pinned by the contract, so we read defensively: `id` is the
// only field we depend on (it's the DELETE key); role identity is resolved from
// whatever the row exposes (roleId / roleKey / nested role) joined to the roles list.
export interface Assignment {
  id: string;
  roleId?: string;
  roleKey?: string;
  roleLabel?: string;
  role?: Partial<Role>;
  scope?: RoleScope;
  /**
   * Compensation/payroll authorization Phase 2: WHERE the role applies.
   * null/absent = not entity-scoped (pre-Phase-2 behaviour).
   */
  entityScope?: EntityScope | null;
  legalEntityIds?: string[];
  /** ISO timestamp the assignment stops conferring access; null = no end. */
  expiresAt?: string | null;
  revokedAt?: string | null;
  /** false once expired or revoked (the row stays for the audit trail). */
  active?: boolean;
  [k: string]: unknown;
}

export type EntityScope = 'ALL_AUTHORIZED_ENTITIES' | 'SELECTED_ENTITIES';

/** Optional assignment scope fields accepted by POST / PATCH rbac/assignments. */
export interface AssignmentScopeInput {
  /** null = not entity-scoped. Omit to leave unchanged (PATCH) / unscoped (POST). */
  entityScope?: EntityScope | null;
  /** Required (non-empty) with SELECTED_ENTITIES, forbidden otherwise. */
  legalEntityIds?: string[];
  /** ISO timestamp in the future; null clears it. */
  expiresAt?: string | null;
  /**
   * Phase 7: continue even though this, the company's FIRST company-scoped
   * assignment, turns company scoping on and leaves the people the server named
   * (409 entity_scope_not_ready) without payroll access. Only send it after the
   * admin confirmed that list.
   */
  acknowledgeLockout?: boolean;
}

// ---- Catalog ---------------------------------------------------------------
/**
 * The permissions THIS tenant may use, grouped by service. Defensive: if the
 * backend returns a flat `permissions[]` (or a bare array), we group client-side
 * by the service prefix before the first ':' in each key.
 */
export async function getCatalog(): Promise<RbacCatalog> {
  const { data } = await api.get(`${BASE}/catalog`);
  return normalizeCatalog(data);
}

function normalizeCatalog(data: unknown): RbacCatalog {
  const d = data as any;
  if (d && Array.isArray(d.services)) {
    return {
      services: d.services.map((s: any) => ({
        service: String(s.service ?? s.name ?? 'other'),
        permissions: (Array.isArray(s.permissions) ? s.permissions : []).map(coercePermission),
      })),
    };
  }
  // Flat list: either { permissions: [...] } or a bare array.
  const flat: any[] = Array.isArray(d?.permissions) ? d.permissions : Array.isArray(d) ? d : [];
  return { services: groupFlat(flat.map(coercePermission)) };
}

function coercePermission(p: any): CatalogPermission {
  if (typeof p === 'string') return { key: p };
  return {
    key: String(p?.key ?? p?.permission ?? p?.name ?? ''),
    description: p?.description ?? p?.desc ?? undefined,
    dataScope: p?.dataScope ?? p?.data_scope ?? undefined,
  };
}

function groupFlat(perms: CatalogPermission[]): CatalogService[] {
  const byService = new Map<string, CatalogPermission[]>();
  for (const p of perms) {
    if (!p.key) continue;
    const service = p.key.includes(':') ? p.key.slice(0, p.key.indexOf(':')) : 'other';
    if (!byService.has(service)) byService.set(service, []);
    byService.get(service)!.push(p);
  }
  return [...byService.entries()]
    .map(([service, permissions]) => ({ service, permissions }))
    .sort((a, b) => a.service.localeCompare(b.service));
}

// ---- Roles -----------------------------------------------------------------
export async function getRoles(): Promise<RolesResponse> {
  const { data } = await api.get(`${BASE}/roles`);
  return {
    systemRoles: (Array.isArray(data?.systemRoles) ? data.systemRoles : []).map(coerceRole(true)),
    customRoles: (Array.isArray(data?.customRoles) ? data.customRoles : []).map(coerceRole(false)),
  };
}

const coerceRole =
  (systemDefault: boolean) =>
  (r: any): Role => ({
    id: String(r?.id ?? r?.key ?? ''),
    key: String(r?.key ?? ''),
    label: String(r?.label ?? r?.name ?? r?.key ?? ''),
    description: r?.description ?? undefined,
    scope: (r?.scope ?? 'tenant') as RoleScope,
    permissions: Array.isArray(r?.permissions) ? r.permissions.map(String) : [],
    isSystem: typeof r?.isSystem === 'boolean' ? r.isSystem : systemDefault,
  });

export async function createRole(body: CreateRoleInput): Promise<Role> {
  const { data } = await api.post(`${BASE}/roles`, body);
  return coerceRole(false)(data ?? body);
}

export async function updateRole(id: string, body: UpdateRoleInput): Promise<Role> {
  const { data } = await api.patch(`${BASE}/roles/${id}`, body);
  return coerceRole(false)(data ?? {});
}

export async function deleteRole(id: string): Promise<void> {
  await api.delete(`${BASE}/roles/${id}`);
}

// ---- Management scope policy (org-scope Phase B) ---------------------------
// The tenant-configurable half of Capability×Scope: a role answers "what may
// this person do"; a policy row answers "over WHICH population". Rows are
// OVERRIDES — absence means every subject resolves to its conservative default.
// Tenant-scoped server-side from the session; we never send a tenantId.
export type PolicyScope =
  | 'SELF'
  | 'DIRECT_REPORTS'
  | 'REPORTING_TREE'
  | 'DEPARTMENT'
  | 'BUSINESS_UNIT'
  | 'BRANCH'
  | 'TENANT'
  | 'EXPLICIT';

export type ScopeSubjectKind =
  | 'role'
  | 'custom_role'
  | 'position'
  | 'management_level'
  | 'department_head'
  | 'business_unit_head';

export interface ScopePolicyRow {
  subjectKind: ScopeSubjectKind;
  subjectKey: string;
  permissionGroup: string; // service prefix e.g. "leave" | "*"
  scope: PolicyScope;
}
export interface ScopeMeta {
  value: PolicyScope;
  // false → stored but enforced conservatively (a subset) until its population
  // resolver lands in a later release.
  fullyEnforced: boolean;
}
export interface ScopeDefault {
  subjectKind: ScopeSubjectKind;
  subjectKey: string;
  label?: string;
  defaultScope: PolicyScope;
}
export interface ScopePolicyResponse {
  scopes: ScopeMeta[];
  subjectKinds: ScopeSubjectKind[];
  defaults: ScopeDefault[];
  policies: ScopePolicyRow[];
}

export async function getScopePolicy(): Promise<ScopePolicyResponse> {
  const { data } = await api.get(`${BASE}/scope-policy`);
  return {
    scopes: Array.isArray(data?.scopes) ? data.scopes : [],
    subjectKinds: Array.isArray(data?.subjectKinds) ? data.subjectKinds : [],
    defaults: Array.isArray(data?.defaults) ? data.defaults : [],
    policies: Array.isArray(data?.policies) ? data.policies : [],
  };
}

export async function saveScopePolicy(policies: ScopePolicyRow[]): Promise<ScopePolicyRow[]> {
  const { data } = await api.put(`${BASE}/scope-policy`, { policies });
  return Array.isArray(data?.policies) ? data.policies : policies;
}

// ---- Approval delegation (org-scope Phase D) ------------------------------
// Self-service: "while I'm away, let X handle my approvals for these
// capabilities, between these dates." A delegate never gains more than the
// delegator held. Tenant + delegator identity come from the session.
export type DelegationScopeType = 'delegator_population' | 'department' | 'branch' | 'employees';
export type DelegationStatus =
  | 'draft'
  | 'pending_approval'
  | 'scheduled'
  | 'active'
  | 'expired'
  | 'revoked'
  | 'rejected'
  | 'pending_acceptance'
  | 'declined';

/** Tenant delegation / coverage policy (auth-service tenant.settings.delegation). */
export interface DelegationPolicy {
  version: number;
  updatedAt: string | null;
  updatedBy: string | null;
  requireForLeave: { mode: 'disabled' | 'managers' | 'everyone' | 'subjects'; subjects: Array<{ kind: 'role' | 'user_type' | 'org_level'; key: string }> };
  confirmation: 'not_required' | 'before_submission' | 'before_final_approval';
  cc: { mode: 'optional' | 'required'; autoManager: boolean; autoHr: boolean; allowManual: boolean };
  eligibleDelegates: { rule: 'any' | 'same_department' | 'same_or_higher_org_level'; roles: string[] };
  dateBuffers: { startDaysBefore: number; endDaysAfter: number };
}

export interface DelegationPolicyEvaluation {
  required: boolean;
  confirmation: DelegationPolicy['confirmation'];
  cc: DelegationPolicy['cc'];
  eligibleDelegates: DelegationPolicy['eligibleDelegates'];
  dateBuffers: DelegationPolicy['dateBuffers'];
  version: number;
}

export interface Delegation {
  id: string;
  delegatorEmployeeId: string;
  delegatorUserId: string | null;
  delegateEmployeeId: string;
  delegateUserId: string | null;
  /** Display names resolved server-side (ids stay for audit/navigation). */
  delegatorName?: string | null;
  delegateName?: string | null;
  capabilities: string[];
  startsAt: string;
  endsAt: string;
  reason: string | null;
  /** Handover note shown where only one fits (older readers): the arranging
   *  manager's note if there is one, else the employee's. */
  note?: string | null;
  /** The two handover notes, each its author's (2026-10-25). Absent from an
   *  older auth — then `note` is the only one. Null when hidden from the reader. */
  employeeNote?: string | null;
  employeeNoteByUserId?: string | null;
  employeeNoteByName?: string | null;
  managerNote?: string | null;
  managerNoteByUserId?: string | null;
  managerNoteByName?: string | null;
  /** auth #42: the stored note's writer was never recorded. The arranging
   *  manager then gets no note text at all, only this flag. */
  noteAuthorUnconfirmed?: boolean;
  /** Cover a manager arranged for the delegator: who arranged it. */
  arrangedByUserId?: string | null;
  /** Human labels for the capabilities (never render keys). */
  capabilityLabels?: string[];
  scopeSummary?: string;
  // Delegation ↔ Leave workflow.
  acceptedAt?: string | null;
  declinedAt?: string | null;
  declineReason?: string | null;
  /** Visibility-only recipients (never authority). */
  ccUserIds?: string[];
  leaveRequestId?: string | null;
  origin?: 'manual' | 'leave';
  delegationPolicyVersion?: string | null;
  // Phase 2+ fields (older rows may omit them — treat as defaults).
  scopeType?: DelegationScopeType;
  scopeRefs?: string[];
  mode?: string;
  status?: DelegationStatus;
  sensitivity?: 'standard' | 'sensitive';
  approvedBy?: string | null;
  approvedAt?: string | null;
  revokedAt: string | null;
  revokedBy?: string | null;
  revokeReason?: string | null;
  active: boolean;
  createdAt: string;
}
export interface CreateDelegationInput {
  delegateEmployeeId: string;
  delegateUserId?: string | null;
  capabilities: string[];
  startsAt: string; // ISO
  endsAt: string; // ISO
  reason?: string;
  note?: string;
  /** Visibility-only CC recipients (auth user ids). */
  ccUserIds?: string[];
  /** Link to the leave this delegation covers; origin=leave follows its lifecycle. */
  leaveRequestId?: string | null;
  origin?: 'manual' | 'leave';
  scopeType?: DelegationScopeType;
  scopeRefs?: string[];
}
export interface MyDelegations {
  asDelegator: Delegation[];
  asDelegate: Delegation[];
  /** Cover the caller arranged for someone else — their own note only. */
  arrangedByMe?: Delegation[];
  /** False when auth couldn't check which arranged cover the caller still
   *  reaches (leave-service unreachable / not configured): arrangedByMe is
   *  then empty because it is UNKNOWN, not because there is none. */
  arrangedByMeAvailable?: boolean;
}

export async function getDelegations(): Promise<MyDelegations> {
  const { data } = await api.get(`${BASE}/delegations`);
  return {
    asDelegator: Array.isArray(data?.asDelegator) ? data.asDelegator : [],
    asDelegate: Array.isArray(data?.asDelegate) ? data.asDelegate : [],
    arrangedByMe: Array.isArray(data?.arrangedByMe) ? data.arrangedByMe : [],
    // An auth without the field (older) is taken at its word, as before.
    arrangedByMeAvailable: data?.arrangedByMeAvailable !== false,
  };
}

export async function createDelegation(input: CreateDelegationInput): Promise<Delegation> {
  const { data } = await api.post(`${BASE}/delegations`, input);
  return data as Delegation;
}

/** The delegate accepts a delegation awaiting their confirmation. */
export async function acceptDelegation(id: string): Promise<Delegation> {
  const { data } = await api.post(`${BASE}/delegations/${id}/accept`, {});
  return data as Delegation;
}

/** The delegate declines (the delegator is told and can choose someone else). */
export async function declineDelegation(id: string, reason?: string): Promise<Delegation> {
  const { data } = await api.post(`${BASE}/delegations/${id}/decline`, reason ? { reason } : {});
  return data as Delegation;
}

/** Delegator/admin changes window, note, CC or the linked leave request. */
export async function updateDelegation(
  id: string,
  input: { startsAt?: string; endsAt?: string; note?: string | null; ccUserIds?: string[]; leaveRequestId?: string | null },
): Promise<Delegation> {
  const { data } = await api.patch(`${BASE}/delegations/${id}`, input);
  return data as Delegation;
}

/** Edit the CALLER's own handover note (the employee's, or — on cover they
 *  arranged — the manager's). Only the note; the server refuses anything else,
 *  and a delegation that is over answers 409. */
/** A delegation in force where the caller is the delegate (auth
 *  GET /rbac/delegations/active-for-me): what they may do, and for whom. */
export interface ActiveDelegationForMe {
  id: string;
  delegatorUserId: string | null;
  delegatorEmployeeId: string | null;
  capabilities: string[];
  scopeType?: string | null;
  scopeRefs?: string[] | null;
}

/** The caller's delegations in force right now (pending, unapproved-leave and
 *  coverage-only ones confer nothing and are not returned). */
export async function getActiveDelegationsForMe(): Promise<ActiveDelegationForMe[]> {
  const { data } = await api.get(`${BASE}/delegations/active-for-me`);
  return Array.isArray(data) ? data : [];
}

export async function editDelegationNote(id: string, note: string | null): Promise<Delegation> {
  const { data } = await api.patch(`${BASE}/delegations/${id}/notes`, { note });
  return data as Delegation;
}

/** The delegation(s) covering a leave request ("Coverage during leave"). */
export async function getDelegationsForLeave(leaveRequestId: string): Promise<Delegation[]> {
  const { data } = await api.get(`${BASE}/delegations/by-leave/${leaveRequestId}`);
  return Array.isArray(data) ? (data as Delegation[]) : [];
}

export async function getDelegationPolicy(): Promise<DelegationPolicy> {
  const { data } = await api.get(`${BASE}/delegations/policy`);
  return data as DelegationPolicy;
}

export async function saveDelegationPolicy(input: Partial<DelegationPolicy>): Promise<DelegationPolicy> {
  const { data } = await api.put(`${BASE}/delegations/policy`, input);
  return data as DelegationPolicy;
}

/** "Must I delegate before taking leave?" for the caller. */
export async function evaluateDelegationPolicy(params: { isManager?: boolean; orgLevel?: string } = {}): Promise<DelegationPolicyEvaluation> {
  const { data } = await api.get(`${BASE}/delegations/policy/evaluate`, {
    params: { is_manager: params.isManager === undefined ? undefined : String(params.isManager), org_level: params.orgLevel },
  });
  return data as DelegationPolicyEvaluation;
}

/** Revoke, optionally with a reason (recorded on the immutable audit trail). */
export async function revokeDelegation(id: string, reason?: string): Promise<void> {
  await api.delete(`${BASE}/delegations/${id}`, { data: reason ? { reason } : undefined });
}

/** Company-Admin approval queue — delegations awaiting approval in the tenant. */
export async function getPendingDelegations(): Promise<Delegation[]> {
  const { data } = await api.get(`${BASE}/delegations/pending-approval`);
  return Array.isArray(data) ? (data as Delegation[]) : [];
}

export async function approveDelegation(id: string, reason?: string): Promise<Delegation> {
  const { data } = await api.post(`${BASE}/delegations/${id}/approve`, reason ? { reason } : {});
  return data as Delegation;
}

export async function rejectDelegation(id: string, reason?: string): Promise<Delegation> {
  const { data } = await api.post(`${BASE}/delegations/${id}/reject`, reason ? { reason } : {});
  return data as Delegation;
}

// ---- Assignments -----------------------------------------------------------
export async function getAssignments(userId: string): Promise<Assignment[]> {
  const { data } = await api.get(`${BASE}/assignments`, { params: { userId } });
  const rows = Array.isArray(data) ? data : Array.isArray(data?.assignments) ? data.assignments : [];
  return rows.map(
    (a: any): Assignment => ({
      ...a,
      id: String(a?.id ?? ''),
      roleId: a?.roleId != null ? String(a.roleId) : a?.role?.id != null ? String(a.role.id) : undefined,
      roleKey: a?.roleKey ?? a?.role?.key ?? undefined,
      roleLabel: a?.roleLabel ?? a?.role?.label ?? undefined,
    }),
  );
}

export async function assignRole(
  input: { userId: string; roleId: string } & AssignmentScopeInput,
): Promise<Assignment> {
  const { data } = await api.post(`${BASE}/assignments`, input);
  return { ...(data ?? {}), id: String(data?.id ?? '') };
}

/** Change where an existing assignment applies and/or when it ends. */
export async function updateAssignmentScope(
  assignmentId: string,
  input: AssignmentScopeInput,
): Promise<Assignment> {
  const { data } = await api.patch(`${BASE}/assignments/${assignmentId}`, input);
  return { ...(data ?? {}), id: String(data?.id ?? assignmentId) };
}

// ---- Effective access preview (compensation/payroll authz Phase 5a) --------
// GET rbac/users/:userId/effective-access — what a person can actually do and
// WHERE, resolved server-side from the same sources enforcement uses.

/**
 * none         — does not hold the permission
 * all          — every company
 * selected     — exactly `entityIds`
 * unrestricted — unscoped, and the company does not use entity scoping
 * no_entities  — holds it, but no company is granted (may see nothing)
 */
export type ReachMode = 'none' | 'all' | 'selected' | 'unrestricted' | 'no_entities';
export type AccessWarningSeverity = 'critical' | 'warning' | 'info';

export interface AccessWarning {
  code: string;
  severity: AccessWarningSeverity;
  message: string;
  permissions?: string[];
  /** Plain-words surfaces ("payroll runs, payslips, filings", "salaries", "accounting (QuickBooks)"). */
  surfaces?: string[];
  allEntities?: boolean;
  entityIds?: string[];
  roleKey?: string;
  roleLabel?: string;
  date?: string;
}

export interface EffectiveAccessRole {
  source: 'base' | 'assignment';
  id?: string;
  roleId?: string;
  roleKey: string;
  roleLabel: string | null;
  scope: string | null;
  entityScope: EntityScope | null;
  legalEntityIds: string[];
  expiresAt: string | null;
  revokedAt?: string | null;
  active: boolean;
  status?: 'active' | 'expired' | 'revoked';
  supersededByScopedAssignment?: boolean;
}

export interface PermissionReachRow {
  permission: string;
  description: string | null;
  mode: ReachMode;
  entityIds: string[];
  delegated: { allEntities: boolean; entityIds: string[] } | null;
}

export interface EffectiveAccess {
  user: { id: string; email: string | null; name: string | null };
  generatedAt: string;
  fullAccess: boolean;
  tenantUsesEntityScoping: boolean;
  roles: EffectiveAccessRole[];
  permissionGroups: { group: string; permissions: { key: string; description: string | null; sensitive: boolean }[] }[];
  permissionCount: number | null;
  sensitiveReach: PermissionReachRow[];
  delegations: { delegatorUserId: string; capabilities: string[]; endsAt: string | null }[];
  warnings: AccessWarning[];
}

export async function getEffectiveAccess(userId: string): Promise<EffectiveAccess> {
  const { data } = await api.get(`${BASE}/users/${encodeURIComponent(userId)}/effective-access`);
  const d = (data ?? {}) as Partial<EffectiveAccess>;
  return {
    user: d.user ?? { id: userId, email: null, name: null },
    generatedAt: d.generatedAt ?? new Date().toISOString(),
    fullAccess: !!d.fullAccess,
    tenantUsesEntityScoping: !!d.tenantUsesEntityScoping,
    roles: Array.isArray(d.roles) ? d.roles : [],
    permissionGroups: Array.isArray(d.permissionGroups) ? d.permissionGroups : [],
    permissionCount: typeof d.permissionCount === 'number' ? d.permissionCount : null,
    sensitiveReach: Array.isArray(d.sensitiveReach) ? d.sensitiveReach : [],
    delegations: Array.isArray(d.delegations) ? d.delegations : [],
    warnings: Array.isArray(d.warnings) ? d.warnings : [],
  };
}

// ---- Company-scope readiness (compensation/payroll authz Phase 7) ----------
// GET rbac/entity-scope/readiness — who holds payroll/compensation permissions
// and whether they would still reach a company once company scoping is on.

export type ReadinessStatus = 'ok' | 'partial' | 'will_lose_access';

export interface ReadinessPerson {
  userId: string;
  name: string | null;
  email: string | null;
  roles: string[];
  status: ReadinessStatus;
  permissions: string[];
  losesPermissions: string[];
  /** The loss in plain words — what they would stop seeing. Absent on older servers. */
  losesSurfaces?: string[];
  reach: { permission: string; mode: ReachMode; entityIds: string[]; viaDelegation: boolean }[];
}

export interface EntityScopeReadiness {
  generatedAt: string;
  tenant: { hasScopedAssignments: boolean; hasGrants: boolean };
  /** count is null when the company list could not be loaded (unknown, not 0). */
  entities: { count: number | null; error: string | null };
  ready: boolean;
  summary: { total: number; ok: number; partial: number; willLoseAccess: number; ownersExcluded: number };
  people: ReadinessPerson[];
}

export async function getEntityScopeReadiness(): Promise<EntityScopeReadiness> {
  const { data } = await api.get(`${BASE}/entity-scope/readiness`);
  const d = (data ?? {}) as Partial<EntityScopeReadiness>;
  const people = Array.isArray(d.people) ? d.people : [];
  if (typeof d.ready !== 'boolean') {
    // A body without a verdict is not "ready" — refuse to guess.
    throw new Error('The readiness check returned an unexpected answer.');
  }
  return {
    generatedAt: d.generatedAt ?? new Date().toISOString(),
    tenant: {
      hasScopedAssignments: !!d.tenant?.hasScopedAssignments,
      hasGrants: !!d.tenant?.hasGrants,
    },
    entities: {
      count: typeof d.entities?.count === 'number' ? d.entities.count : null,
      error: d.entities?.error ?? null,
    },
    ready: d.ready,
    summary: {
      total: d.summary?.total ?? people.length,
      ok: d.summary?.ok ?? 0,
      partial: d.summary?.partial ?? 0,
      willLoseAccess: d.summary?.willLoseAccess ?? 0,
      ownersExcluded: d.summary?.ownersExcluded ?? 0,
    },
    people,
  };
}

/** One person the server says would lose payroll access (409 entity_scope_not_ready). */
export interface LockoutAffected {
  userId: string;
  name: string | null;
  email: string | null;
  permissions: string[];
}

export const ENTITY_SCOPE_NOT_READY = 'entity_scope_not_ready';

/**
 * The lockout refusal from POST/PATCH rbac/assignments, or null for any other
 * error. Only a 409 carrying the exact code counts.
 */
export function entityScopeNotReady(
  e: unknown,
): { message: string; affected: LockoutAffected[]; partial: number } | null {
  const res = (e as { response?: { status?: number; data?: any } })?.response;
  if (res?.status !== 409 || res?.data?.code !== ENTITY_SCOPE_NOT_READY) return null;
  return {
    message: typeof res.data.message === 'string' ? res.data.message : '',
    affected: Array.isArray(res.data.affected) ? res.data.affected : [],
    partial: typeof res.data.partial === 'number' ? res.data.partial : 0,
  };
}

// ---- Scope impact (F5) ------------------------------------------------------
// POST rbac/assignments/:id/scope-impact — for ONE person, what they would LOSE
// if the assignment `<userId>_<roleId>` got this scope (works before the role
// is assigned too). Read-only. Widening reports narrows=false.

export interface ScopeImpactFamily {
  family: string;
  surface: string;
  companiesLost: string[];
  losesAll: boolean;
  lostUnknown: boolean;
  permissions: string[];
}

export interface ScopeImpact {
  userId: string;
  roleId: string;
  narrows: boolean;
  companiesLost: string[];
  surfacesLost: string[];
  surfacesLostEntirely: string[];
  /** Some loss is "every company except …" and the company list was unavailable. */
  companiesUnknown: boolean;
  activatesScoping: boolean;
  families: ScopeImpactFamily[];
}

/** The deterministic assignment id the API uses (`<userId>_<roleId>`). */
export const assignmentIdFor = (userId: string, roleId: string) => `${userId}_${roleId}`;

export async function getScopeImpact(
  assignmentId: string,
  input: Pick<AssignmentScopeInput, 'entityScope' | 'legalEntityIds'>,
): Promise<ScopeImpact> {
  const { data } = await api.post(
    `${BASE}/assignments/${encodeURIComponent(assignmentId)}/scope-impact`,
    { entityScope: input.entityScope ?? null, ...(input.legalEntityIds ? { legalEntityIds: input.legalEntityIds } : {}) },
  );
  const d = (data ?? {}) as Partial<ScopeImpact>;
  if (typeof d.narrows !== 'boolean') {
    // A body without a verdict is not "loses nothing" — refuse to guess.
    throw new Error('The access check returned an unexpected answer.');
  }
  const arr = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
  return {
    userId: d.userId ?? '',
    roleId: d.roleId ?? '',
    narrows: d.narrows,
    companiesLost: arr(d.companiesLost),
    surfacesLost: arr(d.surfacesLost),
    surfacesLostEntirely: arr(d.surfacesLostEntirely),
    companiesUnknown: !!d.companiesUnknown,
    activatesScoping: !!d.activatesScoping,
    families: Array.isArray(d.families) ? d.families : [],
  };
}

export async function unassignRole(assignmentId: string): Promise<void> {
  await api.delete(`${BASE}/assignments/${assignmentId}`);
}

// ---- Entity grants (multi-entity Phase 2) --------------------------------
// Which legal entities a subject may ADMINISTER. Deliberately NOT the entity
// that employs them: Group HR administers several while being employed by
// one, so employment is not authority.
//
// The scope KIND lives on the scope policy (LEGAL_ENTITY); the SELECTION lives
// here. A subject with no grant has NO entity reach — a LEGAL_ENTITY-scoped
// permission then falls back to self, never to everything.
export type EntityGrantSubjectKind = 'user' | 'role' | 'custom_role' | 'api_client';

export interface EntityGrant {
  id: string;
  subjectKind: EntityGrantSubjectKind;
  subjectKey: string;
  permissionGroup: string;
  legalEntityId: string | null;
  allEntities: boolean;
  note?: string | null;
  createdAt: string;
}

export interface EntityGrantsResponse {
  grants: EntityGrant[];
  subjectKinds: EntityGrantSubjectKind[];
}

export async function listEntityGrants(): Promise<EntityGrantsResponse> {
  const { data } = await api.get(`${BASE}/entity-grants`);
  return {
    grants: Array.isArray(data?.grants) ? data.grants : [],
    subjectKinds: Array.isArray(data?.subjectKinds) ? data.subjectKinds : [],
  };
}

export async function createEntityGrant(input: {
  subjectKind: EntityGrantSubjectKind;
  subjectKey: string;
  permissionGroup: string;
  legalEntityId?: string | null;
  allEntities?: boolean;
  note?: string | null;
}): Promise<EntityGrant> {
  const { data } = await api.post(`${BASE}/entity-grants`, input);
  return data;
}

export async function revokeEntityGrant(id: string): Promise<void> {
  await api.delete(`${BASE}/entity-grants/${id}`);
}

// ── O-1: "all companies, including future ones" is for owners only ────────────
export interface AllCompaniesPlan {
  companies: string[];
  assignments: { userId: string; roleId: string; roleKey: string }[];
  grants: { id: string; subjectKind: string; subjectKey: string; permissionGroup: string }[];
  count: number;
}

/** Read-only: every non-owner access that still covers every company (incl. future ones). */
export async function getAllCompaniesAccess(): Promise<AllCompaniesPlan> {
  const { data } = await api.get(`${BASE}/entity-scope/all-companies`);
  const d = (data ?? {}) as Partial<AllCompaniesPlan>;
  if (!Array.isArray(d.assignments) || !Array.isArray(d.grants)) {
    throw new Error('The all-companies check returned an unexpected answer.');
  }
  return {
    companies: Array.isArray(d.companies) ? d.companies : [],
    assignments: d.assignments,
    grants: d.grants,
    count: d.assignments.length + d.grants.length,
  };
}

/** Owner only: convert them to today's companies by name. Nobody loses access today. */
export async function nameAllCompanies(): Promise<{ companies: string[] }> {
  const { data } = await api.post(`${BASE}/entity-scope/all-companies/name`, { apply: true });
  return { companies: Array.isArray(data?.companies) ? data.companies : [] };
}
